import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { avitoService, categoryService, subcategoryService, productService } from '@/application/services';
import type { 
  AvitoSettings,
  UpdateAvitoSettingsRequest,
  AvitoTemplate,
  CreateAvitoTemplateRequest,
  UpdateAvitoTemplateRequest,
  AvitoСategoryMapping,
  CreateAvitoMappingRequest,
  UpdateAvitoMappingRequest,
  AvitoCategory,
  AvitoHealthCheck
} from '@/infrastructure/repositories';
import type { Category } from '@/domain/entities';
import { HttpError } from '@/domain/entities';

type TabType = 'overview' | 'settings' | 'templates' | 'mappings';

export const AvitoIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Settings
  const [settings, setSettings] = useState<AvitoSettings | null>(null);
  const [settingsForm, setSettingsForm] = useState<UpdateAvitoSettingsRequest>({
    storeName: '',
    storeAddress: '',
    idPrefix: '',
    idFormat: 'size_string',
  });

  // Templates
  const [templates, setTemplates] = useState<AvitoTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AvitoTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<CreateAvitoTemplateRequest>({
    categoryKey: '',
    titleTemplate: '',
    descriptionTemplate: '',
  });

  // Mappings
  const [mappings, setMappings] = useState<AvitoСategoryMapping[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categorySubcategories, setCategorySubcategories] = useState<Record<number, any[]>>({});
  const [avitoCategories, setAvitoCategories] = useState<AvitoCategory[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState<AvitoСategoryMapping | null>(null);
  const [mappingForm, setMappingForm] = useState<CreateAvitoMappingRequest>({
    categoryId: 0,
    avitoGender: 'men',
    avitoCategorySlug: 'sneakers',
    avitoApparelType: undefined,
    avitoGoodsSubType: undefined,
  });

  // XML Generation
  const [generating, setGenerating] = useState(false);
  const [xmlResult, setXmlResult] = useState<string | null>(null);
  const [productCount, setProductCount] = useState<number>(0);

  // Health Check
  const [healthStatus, setHealthStatus] = useState<AvitoHealthCheck | null>(null);
  const [checkingHealth, setCheckingHealth] = useState(false);

  useEffect(() => {
    // Используем флаг для предотвращения повторного вызова
    let mounted = true;
    
    if (mounted) {
      loadInitialData();
    }
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // loadInitialData стабильна, navigate стабилен

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Загружаем критически важные данные параллельно
      const [settingsResult, categoriesResult] = await Promise.allSettled([
        avitoService.getSettings(),
        categoryService.getCategories(),
      ]);

      // Обрабатываем settings
      let settingsData: AvitoSettings | null = null;
      if (settingsResult.status === 'fulfilled') {
        settingsData = settingsResult.value;
      } else {
        const err = settingsResult.reason;
        console.error('Failed to load Avito settings', err);
        if (err instanceof HttpError && err.status === 401) {
          navigate('/login');
          return;
        }
        // Показываем предупреждение, но не блокируем интерфейс
        console.warn('Avito settings not available, using defaults');
      }

      // Обрабатываем categories
      let categoriesData: Category[] = [];
      if (categoriesResult.status === 'fulfilled') {
        categoriesData = categoriesResult.value;
      } else {
        console.error('Failed to load categories', categoriesResult.reason);
        // Не блокируем интерфейс если категории не загрузились
        console.warn('Categories not available');
      }

      // Устанавливаем settings сразу, чтобы показать интерфейс
      if (settingsData) {
        setSettings(settingsData);
        setSettingsForm({
          storeName: settingsData.storeName,
          storeAddress: settingsData.storeAddress,
          idPrefix: settingsData.idPrefix,
          idFormat: settingsData.idFormat,
          autoUpdateEnabled: settingsData.autoUpdateEnabled,
        });
      }
      setCategories(categoriesData);

      // Загружаем остальные данные параллельно (не критичные)
      const [templatesResult, mappingsResult, avitoCategoriesResult, productsResult] = await Promise.allSettled([
        avitoService.getAllTemplates(),
        avitoService.getAllMappings(),
        avitoService.getSupportedCategories(),
        productService.getProducts({ limit: 1 }),
      ]);

      // Обрабатываем результаты
      if (templatesResult.status === 'fulfilled') {
        setTemplates(templatesResult.value);
      } else {
        console.error('Failed to load templates', templatesResult.reason);
      }

      if (mappingsResult.status === 'fulfilled') {
        setMappings(mappingsResult.value);
      } else {
        console.error('Failed to load mappings', mappingsResult.reason);
      }

      if (avitoCategoriesResult.status === 'fulfilled') {
        setAvitoCategories(avitoCategoriesResult.value);
      } else {
        console.error('Failed to load Avito categories', avitoCategoriesResult.reason);
      }

      if (productsResult.status === 'fulfilled') {
        const productsResponse = productsResult.value;
        setProductCount(productsResponse?.total ?? productsResponse?.products?.length ?? 0);
      } else {
        console.error('Failed to load product count', productsResult.reason);
      }

      // Загружаем подкатегории только если они нужны (ленивая загрузка)
      // Загружаем их в фоне, не блокируя интерфейс
      if (categoriesData.length > 0) {
        // Загружаем подкатегории асинхронно, не блокируя рендер
        Promise.all(
          categoriesData.map(async (cat) => {
            try {
              const subs = await subcategoryService.getSubcategories(cat.id);
              return { categoryId: cat.id, subcategories: subs };
            } catch (err) {
              console.error(`Failed to load subcategories for category ${cat.id}`, err);
              return { categoryId: cat.id, subcategories: [] };
            }
          })
        ).then((subcategoriesResults) => {
          const subcategoriesMap: Record<number, any[]> = {};
          subcategoriesResults.forEach(({ categoryId, subcategories }) => {
            subcategoriesMap[categoryId] = subcategories;
          });
          setCategorySubcategories(subcategoriesMap);
        });
      }

      // Health check делаем в фоне, не блокируя загрузку страницы
      // Запускаем с небольшой задержкой, чтобы не замедлять основную загрузку
      setTimeout(async () => {
        try {
          const health = await avitoService.healthCheck();
          setHealthStatus(health);
        } catch (err) {
          console.error('Failed to check Avito health', err);
          // Не показываем ошибку пользователю, это не критично
        }
      }, 500);
    } catch (err) {
      console.error('Unexpected error in loadInitialData', err);
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message || 'Не удалось загрузить данные интеграции');
      } else if (err instanceof Error) {
        setError(err.message || 'Не удалось загрузить данные интеграции');
      } else {
        setError('Не удалось загрузить данные интеграции');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Settings Handlers
  // ============================================================================

  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await avitoService.updateSettings(settingsForm);
      setSettings(updated);
      setSuccess('Настройки успешно сохранены');
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сохранить настройки');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Template Handlers
  // ============================================================================

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ categoryKey: '', titleTemplate: '', descriptionTemplate: '' });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template: AvitoTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      categoryKey: template.categoryKey,
      titleTemplate: template.titleTemplate,
      descriptionTemplate: template.descriptionTemplate,
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingTemplate) {
        await avitoService.updateTemplate(editingTemplate.categoryKey, {
          titleTemplate: templateForm.titleTemplate,
          descriptionTemplate: templateForm.descriptionTemplate,
        });
        setSuccess('Шаблон успешно обновлен');
      } else {
        await avitoService.createTemplate(templateForm);
        setSuccess('Шаблон успешно создан');
      }
      const updatedTemplates = await avitoService.getAllTemplates();
      setTemplates(updatedTemplates);
      setShowTemplateModal(false);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сохранить шаблон');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (key: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот шаблон?')) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await avitoService.deleteTemplate(key);
      const updatedTemplates = await avitoService.getAllTemplates();
      setTemplates(updatedTemplates);
      setSuccess('Шаблон успешно удален');
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось удалить шаблон');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Mapping Handlers
  // ============================================================================

  const handleCreateMapping = () => {
    setEditingMapping(null);
    setMappingForm({
      categoryId: categories[0]?.id || 0,
      avitoGender: 'men',
      avitoCategorySlug: 'sneakers',
    });
    setShowMappingModal(true);
  };

  const handleEditMapping = (mapping: AvitoСategoryMapping) => {
    setEditingMapping(mapping);
    setMappingForm({
      categoryId: mapping.categoryId,
      subcategoryId: mapping.subcategoryId || undefined,
      avitoGender: mapping.avitoGender,
      avitoCategorySlug: mapping.avitoCategorySlug,
    });
    setShowMappingModal(true);
  };

  const handleSaveMapping = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingMapping) {
        await avitoService.updateMapping(editingMapping.id, {
          avitoGender: mappingForm.avitoGender,
          avitoCategorySlug: mappingForm.avitoCategorySlug,
        });
        setSuccess('Маппинг успешно обновлен');
      } else {
        await avitoService.createMapping(mappingForm);
        setSuccess('Маппинг успешно создан');
      }
      const updatedMappings = await avitoService.getAllMappings();
      setMappings(updatedMappings);
      setShowMappingModal(false);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сохранить маппинг');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (id: number) => {
    if (!confirm('Вы уверены, что хотите удалить этот маппинг?')) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await avitoService.deleteMapping(id);
      const updatedMappings = await avitoService.getAllMappings();
      setMappings(updatedMappings);
      setSuccess('Маппинг успешно удален');
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось удалить маппинг');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // XML Generation Handler
  // ============================================================================

  const handleGenerateXML = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    setXmlResult(null);
    try {
      const result = await avitoService.generateXML();
      setXmlResult(result.message);
      setSuccess('XML успешно сгенерирован');
      // Обновляем настройки чтобы получить новое время генерации
      const updatedSettings = await avitoService.getSettings();
      setSettings(updatedSettings);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сгенерировать XML');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!confirm('Вы уверены? Старая ссылка перестанет работать!')) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await avitoService.regenerateXMLToken();
      setSuccess(`Новый токен сгенерирован!`);
      // Обновляем настройки чтобы получить новый токен
      const updatedSettings = await avitoService.getSettings();
      setSettings(updatedSettings);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сгенерировать новый токен');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckHealth = async () => {
    setCheckingHealth(true);
    setError(null);
    try {
      const health = await avitoService.healthCheck();
      setHealthStatus(health);
      setSuccess('Статус проверен успешно');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message || 'Не удалось проверить статус');
      } else {
        setError('Не удалось проверить статус интеграции');
      }
    } finally {
      setCheckingHealth(false);
    }
  };

  const handleDownloadXML = async () => {
    if (!settings?.xmlUrl && !settings?.xmlToken) {
      setError('XML URL не найден. Сначала сгенерируйте XML.');
      return;
    }

    try {
      const xmlUrl = settings.xmlUrl || `${window.location.origin}/api/public/avito/xml/${settings.xmlToken}`;
      
      // Открываем XML в новой вкладке для скачивания
      const link = document.createElement('a');
      link.href = xmlUrl;
      link.target = '_blank';
      link.download = 'avito_products.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('XML файл открыт в новой вкладке');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Не удалось скачать XML файл');
    }
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const getCategoryName = (categoryId: number): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.nameRu || category?.nameEn || `ID: ${categoryId}`;
  };

  const getSubcategoryName = (categoryId: number, subcategoryId: number | null): string => {
    if (!subcategoryId) return '—';
    const subcategories = categorySubcategories[categoryId] || [];
    const subcategory = subcategories.find(s => s.id === subcategoryId);
    return subcategory?.nameRu || subcategory?.nameEn || `ID: ${subcategoryId}`;
  };

  const getAvitoCategoryName = (gender: string, slug: string): string => {
    const category = avitoCategories.find(
      c => c.example.gender === gender && c.example.categorySlug === slug
    );
    return category?.name || `${gender}_${slug}`;
  };

  const getSubcategories = (categoryId: number) => {
    return categorySubcategories[categoryId] || [];
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Показываем загрузку только при первой загрузке, если нет settings
  // Это позволяет показывать интерфейс даже если некоторые данные еще загружаются
  if (loading && !settings && !error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
          <div className="text-lg text-gray-600 dark:text-gray-400">Загрузка...</div>
        </div>
      </div>
    );
  }

  // Не показываем критическую ошибку если есть хотя бы settings или templates/mappings
  // Это позволяет работать со страницей даже если часть данных недоступна
  const hasAnyData = settings || templates.length > 0 || mappings.length > 0 || categories.length > 0;
  
  if (!loading && !hasAnyData && error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Ошибка загрузки</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Обновить страницу
            </button>
            <button
              onClick={() => {
                setError(null);
                loadInitialData();
              }}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            onClick={() => navigate('/integration')}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Назад к интеграциям
          </button>
          <div className="flex items-center gap-4 mb-2">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
              <svg className="w-8 h-8" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10.595" cy="5.225" r="3.325" fill="#965EEB"></circle>
                <circle cx="22.245" cy="7.235" r="7.235" fill="#00AAFF"></circle>
                <circle cx="8.9" cy="18.6" r="8.9" fill="#04E061"></circle>
                <circle cx="24.325" cy="21.005" r="5.375" fill="#FF4053"></circle>
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Авито</h1>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    healthStatus?.status === 'ok' || healthStatus?.status === 'healthy' 
                      ? 'bg-green-500' 
                      : healthStatus?.status === 'error' || healthStatus?.status === 'unhealthy'
                      ? 'bg-red-500'
                      : 'bg-yellow-500'
                  }`}></span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {healthStatus?.status === 'ok' || healthStatus?.status === 'healthy' 
                      ? 'Подключено' 
                      : healthStatus?.status === 'error' || healthStatus?.status === 'unhealthy'
                      ? 'Ошибка подключения'
                      : 'Проверка...'}
                  </span>
                </div>
                <button
                  onClick={handleCheckHealth}
                  disabled={checkingHealth}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:text-gray-400 dark:disabled:text-gray-500 flex items-center gap-1"
                  title="Проверить статус"
                >
                  <svg className={`w-3 h-3 ${checkingHealth ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {checkingHealth ? 'Проверка...' : 'Проверить'}
                </button>
              </div>
              {healthStatus?.timestamp && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Последняя проверка: {new Date(healthStatus.timestamp).toLocaleString('ru-RU')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Обзор
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Настройки
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Шаблоны ({templates.length})
            </button>
            <button
              onClick={() => setActiveTab('mappings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mappings'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Маппинг категорий ({mappings.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 dark:text-indigo-400">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">XML файл с товарами</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Публичная ссылка с токеном для автоматической выгрузки товаров на Авито.</p>
                  </div>
                </div>

                {(settings?.lastXmlGeneration || settings?.lastxmlGeneration) && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 3.5a6.5 6.5 0 016.5 6.5 6.5 6.5 0 01-6.5 6.5A6.5 6.5 0 013.5 10 6.5 6.5 0 0110 3.5z" />
                      <path d="M10 6v4l2.5 1.5" />
                    </svg>
                    <span>Последнее обновление: {new Date(settings.lastXmlGeneration || settings.lastxmlGeneration || '').toLocaleString('ru-RU')}</span>
                  </div>
                )}

                {settings && settings.xmlToken && (
                  <div className="rounded-xl border border-indigo-100 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30 p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-indigo-900 dark:text-indigo-200">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="14" rx="2" ry="2" />
                        <path d="M7 8h10M7 12h6" />
                      </svg>
                      Публичная ссылка на XML
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <input
                        type="text"
                        readOnly
                        value={settings.xmlUrl || (settings.xmlToken ? `${window.location.origin}/api/public/avito/xml/${settings.xmlToken}` : '')}
                        className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-700 border border-indigo-200 dark:border-indigo-700 rounded-lg text-sm font-mono text-indigo-900 dark:text-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <button
                        onClick={() => {
                          const urlToCopy = settings?.xmlUrl || (settings?.xmlToken ? `${window.location.origin}/api/public/avito/xml/${settings.xmlToken}` : '');
                          if (urlToCopy) {
                            navigator.clipboard.writeText(urlToCopy).then(() => {
                              setSuccess('Ссылка скопирована!');
                              setTimeout(() => setSuccess(null), 2000);
                            }).catch(() => {
                              setError('Не удалось скопировать ссылку');
                            });
                          }
                        }}
                        disabled={!settings?.xmlUrl && !settings?.xmlToken}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="4" y="4" width="12" height="12" rx="2" />
                          <path d="M8 4V2h8v12h-2" />
                        </svg>
                        Копировать
                      </button>
                    </div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">
                      Ссылка защищена уникальным токеном — укажите её в настройках автозагрузки на стороне Авито.
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={handleGenerateXML}
                    disabled={generating}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 rounded-lg shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 3v14M3 10h14" />
                    </svg>
                    {generating ? 'Генерация…' : 'Сгенерировать XML'}
                  </button>
                  <button
                    onClick={handleDownloadXML}
                    disabled={!settings?.xmlUrl && !settings?.xmlToken}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
                    title={!settings?.xmlUrl && !settings?.xmlToken ? 'Сначала сгенерируйте XML' : 'Скачать XML файл'}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 3v10m0 0l-3-3m3 3l3-3" />
                      <path d="M3 17h14" />
                    </svg>
                    Скачать XML
                  </button>
                  <button
                    onClick={handleRegenerateToken}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 2v6h-6" />
                      <path d="M3 12a9 9 0 0115-6.7l3 2.7" />
                      <path d="M3 22v-6h6" />
                      <path d="M21 12a9 9 0 01-15 6.7l-3-2.7" />
                    </svg>
                    Новый токен
                  </button>
                </div>

                {xmlResult && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{xmlResult}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Товаров в каталоге</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{productCount}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Шаблонов</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{templates.length}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Маппингов</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{mappings.length}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Категорий Avito</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{avitoCategories.length}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && settings && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Настройки магазина</h2>
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Название магазина
                </label>
                <input
                  type="text"
                  value={settingsForm.storeName}
                  onChange={(e) => setSettingsForm({ ...settingsForm, storeName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Адрес магазина
                </label>
                <input
                  type="text"
                  value={settingsForm.storeAddress}
                  onChange={(e) => setSettingsForm({ ...settingsForm, storeAddress: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Префикс ID
                </label>
                <input
                  type="text"
                  value={settingsForm.idPrefix}
                  onChange={(e) => setSettingsForm({ ...settingsForm, idPrefix: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Формат ID
                </label>
                <select
                  value={settingsForm.idFormat}
                  onChange={(e) => setSettingsForm({ ...settingsForm, idFormat: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="size_string">Строковый (sku_ABC123_42)</option>
                  <option value="size_float">Float (sku_ABC123_42.5)</option>
                </select>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <input
                  type="checkbox"
                  id="autoUpdate"
                  checked={settingsForm.autoUpdateEnabled || false}
                  onChange={(e) => setSettingsForm({ ...settingsForm, autoUpdateEnabled: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 bg-white dark:bg-gray-700"
                />
                <label htmlFor="autoUpdate" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Автоматическое обновление XML каждый час
                </label>
              </div>
              <div className="pt-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Сохранение...' : 'Сохранить настройки'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Шаблоны</h2>
              <button
                onClick={handleCreateTemplate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                + Добавить шаблон
              </button>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ключ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Название</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Описание</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Нет шаблонов
                      </td>
                    </tr>
                  ) : (
                    (Array.isArray(templates) ? templates : []).map((template) => (
                      <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {template.categoryKey}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{template.titleTemplate}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                          {template.descriptionTemplate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-4"
                          >
                            Редактировать
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.categoryKey)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'mappings' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Маппинг категорий</h2>
              <button
                onClick={handleCreateMapping}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                + Добавить маппинг
              </button>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Категория</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Подкатегория</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Пол (Avito)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Категория (Avito)</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {mappings.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Нет маппингов
                      </td>
                    </tr>
                  ) : (
                    (Array.isArray(mappings) ? mappings : []).map((mapping) => (
                      <tr key={mapping.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {getCategoryName(mapping.categoryId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {getSubcategoryName(mapping.categoryId, mapping.subcategoryId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <span className="capitalize">{mapping.avitoGender === 'men' ? 'Мужское' : mapping.avitoGender === 'women' ? 'Женское' : 'Унисекс'}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {getAvitoCategoryName(mapping.avitoGender, mapping.avitoCategorySlug)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEditMapping(mapping)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-4"
                          >
                            Редактировать
                          </button>
                          <button
                            onClick={() => handleDeleteMapping(mapping.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Template Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ключ шаблона
                    </label>
                    {editingTemplate ? (
                      <input
                        type="text"
                        value={templateForm.categoryKey}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    ) : (
                      <select
                        value={templateForm.categoryKey}
                        onChange={(e) => setTemplateForm({ ...templateForm, categoryKey: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Выберите ключ</option>
                        
                        {/* Группируем категории */}
                        <optgroup label="Обувь - Мужская">
                          {(Array.isArray(avitoCategories) ? avitoCategories : [])
                            .filter(cat => cat.example.gender === 'men' && cat.example.categorySlug === 'sneakers')
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                        </optgroup>
                        
                        <optgroup label="Обувь - Женская">
                          {(Array.isArray(avitoCategories) ? avitoCategories : [])
                            .filter(cat => cat.example.gender === 'women' && cat.example.categorySlug === 'sneakers')
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                        </optgroup>
                        
                        <optgroup label="Одежда - Мужская">
                          {(Array.isArray(avitoCategories) ? avitoCategories : [])
                            .filter(cat => cat.example.gender === 'men' && cat.example.categorySlug !== 'sneakers')
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                        </optgroup>
                        
                        <optgroup label="Одежда - Женская">
                          {(Array.isArray(avitoCategories) ? avitoCategories : [])
                            .filter(cat => cat.example.gender === 'women' && cat.example.categorySlug !== 'sneakers')
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                        </optgroup>
                        
                        <optgroup label="Унисекс">
                          {(Array.isArray(avitoCategories) ? avitoCategories : [])
                            .filter(cat => cat.example.gender === 'unisex')
                            .map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                        </optgroup>
                      </select>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Ключ определяет, для какой категории будет использоваться этот шаблон
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Шаблон названия
                    </label>
                    <input
                      type="text"
                      value={templateForm.titleTemplate}
                      onChange={(e) => setTemplateForm({ ...templateForm, titleTemplate: e.target.value })}
                      placeholder="{BRAND} {NAME} | {STORE_NAME}"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Переменные: {'{BRAND}'}, {'{NAME}'}, {'{SKU}'}, {'{COLOR}'}, {'{STORE_NAME}'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Шаблон описания
                    </label>
                    <textarea
                      value={templateForm.descriptionTemplate}
                      onChange={(e) => setTemplateForm({ ...templateForm, descriptionTemplate: e.target.value })}
                      placeholder="💰 {STORE_NAME} - Оригинальная одежда!&#10;{PRODUCT_NAME}&#10;{AVAILABLE_SIZES}..."
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Переменные: {'{BRAND}'}, {'{NAME}'}, {'{PRODUCT_NAME}'}, {'{SKU}'}, {'{COLOR}'}, {'{STORE_NAME}'}, {'{AVAILABLE_SIZES}'}, {'{PRICE}'}
                    </p>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      Поддерживается HTML: &lt;strong&gt;, &lt;p&gt;, &lt;br&gt;, emoji
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowTemplateModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleSaveTemplate}
                    disabled={loading || !templateForm.categoryKey || !templateForm.titleTemplate}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mapping Modal */}
        {showMappingModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full pointer-events-auto border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {editingMapping ? 'Редактировать маппинг' : 'Создать маппинг'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Категория
                    </label>
                    <select
                      value={mappingForm.categoryId}
                      onChange={(e) => setMappingForm({ ...mappingForm, categoryId: Number(e.target.value), subcategoryId: undefined })}
                      disabled={!!editingMapping}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700"
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nameRu || cat.nameEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Подкатегория (опционально)
                    </label>
                    <select
                      value={mappingForm.subcategoryId || ''}
                      onChange={(e) => setMappingForm({ ...mappingForm, subcategoryId: e.target.value ? Number(e.target.value) : undefined })}
                      disabled={!!editingMapping}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700"
                    >
                      <option value="">Без подкатегории</option>
                      {getSubcategories(mappingForm.categoryId).map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.nameRu || sub.nameEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Пол (Avito)
                    </label>
                    <select
                      value={mappingForm.avitoGender}
                      onChange={(e) => setMappingForm({ ...mappingForm, avitoGender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="men">Мужское</option>
                      <option value="women">Женское</option>
                      <option value="unisex">Унисекс</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Категория (Avito)
                    </label>
                    <select
                      value={mappingForm.avitoCategorySlug}
                      onChange={(e) => {
                        // Находим выбранную категорию
                        const selectedCategory = avitoCategories.find(cat => 
                          cat.example.categorySlug === e.target.value && 
                          cat.example.gender === mappingForm.avitoGender
                        );
                        
                        setMappingForm({ 
                          ...mappingForm, 
                          avitoCategorySlug: e.target.value,
                          avitoApparelType: selectedCategory?.example.apparelType,
                          avitoGoodsSubType: selectedCategory?.example.goodsSubType,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {(Array.isArray(avitoCategories) ? avitoCategories : [])
                        .filter(cat => cat.example.gender === mappingForm.avitoGender)
                        .map((cat) => (
                          <option key={cat.id} value={cat.example.categorySlug}>
                            {cat.name} ({cat.example.categorySlug})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowMappingModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleSaveMapping}
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
